import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";

import type { PlatePage } from "./types";

type PageNavigatorProps = {
  pages: PlatePage[];
  currentPageId: string;
  readOnly: boolean;
  onSelect: (pageId: string) => void;
  onAdd: () => void;
  onDelete: (pageId: string) => void;
  onRename: (pageId: string, name: string) => void;
};

export const PageNavigator: React.FC<PageNavigatorProps> = ({
  pages,
  currentPageId,
  readOnly,
  onSelect,
  onAdd,
  onDelete,
  onRename,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  // Collapsed by default on mobile (<640px), expanded on desktop
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 640) setCollapsed(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const startEdit = (page: PlatePage) => {
    if (readOnly) return;
    setEditingId(page.id);
    setDraftName(page.name);
  };

  const commitEdit = () => {
    if (editingId) {
      const trimmed = draftName.trim();
      if (trimmed) onRename(editingId, trimmed);
    }
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  // Collapsed: show only a narrow strip with toggle button
  if (collapsed) {
    return (
      <div
        data-testid="plate-page-nav"
        className="w-8 shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col items-center pt-2 gap-1"
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="p-1 rounded hover:bg-gray-200 text-gray-500 min-h-[36px]"
          aria-label="Expand page navigator"
          title={`Pages (${pages.length})`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <span className="text-[10px] text-gray-400 font-semibold" style={{ writingMode: 'vertical-rl' }}>
          {pages.length}p
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="plate-page-nav"
      className="w-44 sm:w-48 shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col"
    >
      <div className="p-2 sm:p-3 border-b border-gray-200 flex items-center justify-between">
        <span data-testid="plate-page-count" className="text-xs font-semibold uppercase text-gray-600">
          Pages ({pages.length})
        </span>
        <div className="flex items-center gap-1">
          {!readOnly && (
            <button
              type="button"
              data-testid="plate-add-page"
              onClick={onAdd}
              className="p-1 rounded hover:bg-gray-200 text-gray-700 min-h-[30px] min-w-[30px] flex items-center justify-center"
              aria-label="Add page"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
          {/* Collapse button — mobile only */}
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="sm:hidden p-1 rounded hover:bg-gray-200 text-gray-500 min-h-[30px] min-w-[30px] flex items-center justify-center"
            aria-label="Collapse page navigator"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {pages.map((page, idx) => {
          const isEditing = editingId === page.id;
          return (
            <div
              key={page.id}
              data-testid="plate-page-item"
              className={`group flex items-center justify-between rounded px-2 py-1.5 text-sm ${
                page.id === currentPageId
                  ? "bg-emerald-100 text-emerald-900 font-medium"
                  : "hover:bg-gray-100 text-gray-700"
              } ${isEditing ? "" : "cursor-pointer"}`}
              onClick={() => !isEditing && onSelect(page.id)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                startEdit(page);
              }}
              title={readOnly ? undefined : "Double-click to rename"}
            >
              {isEditing ? (
                <input
                  data-testid="plate-page-name-input"
                  value={draftName}
                  autoFocus
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitEdit();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      cancelEdit();
                    }
                  }}
                  onBlur={commitEdit}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 min-w-0 bg-white border border-emerald-400 rounded px-1 py-0.5 text-sm outline-none focus:ring-1 focus:ring-emerald-500"
                />
              ) : (
                <span className="truncate">
                  {idx + 1}. {page.name}
                </span>
              )}
              {!readOnly && !isEditing && (
                <div className="flex items-center opacity-0 group-hover:opacity-100">
                  <button
                    type="button"
                    data-testid="plate-page-edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEdit(page);
                    }}
                    className="p-0.5 text-gray-500 hover:text-emerald-600"
                    aria-label="Rename page"
                    title="Rename page"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {pages.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(page.id);
                      }}
                      className="p-0.5 text-gray-500 hover:text-red-600"
                      aria-label="Delete page"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
